import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';
import { apiFetch, ApiError } from '../utils/apiFetch';
import { showToast } from '../components/Toast/Toast';

/**
 * Hook for fetching blog/insights data
 */
export function useInsights(options = {}) {
  const {
    limit = 20,
    offset = 0,
    category = null,
    search = null,
    lang = null,
    autoFetch = true
  } = options;

  // Check for server-injected data (SSR) for initial page load
  const ssrList = typeof window !== 'undefined' && window.__INSIGHTS_LIST__;
  const hasSSRData = ssrList && ssrList.posts && ssrList.posts.length > 0 && offset === 0 && !category && !search;

  // Persist SSR flag in ref — survives window.__INSIGHTS_LIST__ deletion and
  // re-renders caused by LanguageContext lang change (en → detected lang).
  const ssrHitRef = useRef(hasSSRData);

  const [posts, setPosts] = useState(hasSSRData ? ssrList.posts : []);
  const [total, setTotal] = useState(hasSSRData ? ssrList.total : 0);
  const [loading, setLoading] = useState(!hasSSRData);
  const [error, setError] = useState(null);

  const fetchPosts = useCallback(async (overrideOptions = {}) => {
    const isSSRHit = ssrHitRef.current;
    try {
      if (!isSSRHit) {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams();
      params.set('limit', overrideOptions.limit ?? limit);
      params.set('offset', overrideOptions.offset ?? offset);

      if (overrideOptions.category ?? category) {
        params.set('category', overrideOptions.category ?? category);
      }
      if (overrideOptions.search ?? search) {
        params.set('search', overrideOptions.search ?? search);
      }
      if (overrideOptions.lang ?? lang) {
        params.set('lang', overrideOptions.lang ?? lang);
      }

      const response = await fetch(`${API_BASE_URL}/api/blog/posts?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setPosts(data.posts || []);
        setTotal(data.total || 0);
        ssrHitRef.current = false;
      } else if (!isSSRHit) {
        setError(data.error || 'Failed to fetch posts');
      }
    } catch (err) {
      if (!isSSRHit) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [limit, offset, category, search, lang]);

  useEffect(() => {
    // Clean up SSR data after first use
    if (window.__INSIGHTS_LIST__) {
      delete window.__INSIGHTS_LIST__;
    }

    if (autoFetch) {
      fetchPosts();
    }
  }, [fetchPosts, autoFetch]);

  return {
    posts,
    total,
    loading,
    error,
    refetch: fetchPosts
  };
}

/**
 * Hook for fetching a single blog post by slug
 */
export function useInsightPost(slug, lang = null) {
  // Check for server-injected data (SSR) to avoid API call and "Article Not Found" flash
  const ssrData = typeof window !== 'undefined' && window.__INSIGHT_DATA__;
  const hasSSRData = ssrData && ssrData.post && ssrData.post.slug === slug;

  // Persist SSR flag in a ref so it survives across re-renders even after
  // window.__INSIGHT_DATA__ is deleted. This prevents "Article Not Found" when
  // LanguageContext triggers a lang change (en → ru) that re-runs this effect
  // and the API is unreachable (e.g., Google's WRS blocks /api/ via robots.txt).
  const ssrHitRef = useRef(hasSSRData);

  const [post, setPost] = useState(hasSSRData ? ssrData.post : null);
  const [related, setRelated] = useState(hasSSRData ? (ssrData.related || []) : []);
  const [loading, setLoading] = useState(!hasSSRData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;

    const isSSRHit = ssrHitRef.current;

    const fetchPost = async () => {
      try {
        if (!isSSRHit) {
          setLoading(true);
          setError(null);
        }

        const params = new URLSearchParams();
        if (lang) params.set('lang', lang);
        const qs = params.toString();
        const data = await apiFetch(`/api/blog/posts/${encodeURIComponent(slug)}${qs ? '?' + qs : ''}`);

        if (data.success) {
          setPost(data.post);
          setRelated(data.related || []);
          ssrHitRef.current = false;
        } else if (!isSSRHit) {
          setError('not_found');
        }
      } catch (err) {
        if (!isSSRHit) {
          if (err instanceof ApiError && err.status === 404) {
            setError('not_found');
          } else if (err instanceof ApiError && err.retryable) {
            setError('service_error');
            showToast('Server is temporarily unavailable. Please try again in a moment.', 'warning', 6000);
          } else {
            setError('service_error');
            showToast('Failed to load article. Please try again later.', 'error');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();

    // Clean up SSR data after first use
    if (window.__INSIGHT_DATA__) {
      delete window.__INSIGHT_DATA__;
    }
  }, [slug, lang]);

  return {
    post,
    related,
    loading,
    error
  };
}

/**
 * Hook for fetching category statistics
 */
export function useInsightCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/api/blog/categories`);
        const data = await response.json();

        if (data.success) {
          setCategories(data.categories || []);
        } else {
          setError(data.error || 'Failed to fetch categories');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error
  };
}

/**
 * Hook for fetching random "Other Analysis" posts (excluding current post)
 */
export function useOtherAnalysis(excludeSlug, limit = 4, lang = null) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!excludeSlug) return;

    const fetchOtherAnalysis = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.set('limit', limit);
        params.set('exclude', excludeSlug);
        if (lang) params.set('lang', lang);

        const response = await fetch(`${API_BASE_URL}/api/blog/other-analysis?${params.toString()}`);
        const data = await response.json();

        if (data.success) {
          setPosts(data.posts || []);
        } else {
          setError(data.error || 'Failed to fetch other analysis');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOtherAnalysis();
  }, [excludeSlug, limit, lang]);

  return {
    posts,
    loading,
    error
  };
}
